import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, Self } from '@angular/core';
import { AuthenticationService } from '../../services/authentication.service';
import { Observable } from 'rxjs';
import { IContact, IDataLayer, ILoanProduct } from '@libs/models/models';
import { ICalculatePreview } from '@libs/interfaces/calculate-preview.interface';
import { CalculateStore } from '../../storage/calculate.store';
import { ClientStore } from '../../storage/client.store';
import { DeviceService } from '@libs/services/device.service';
import { DATA_LAYER, ENVIRONMENT, LOCATION, SESSION_STORAGE, WINDOW } from '../../injections/window';
import { NavItem } from '@libs/models/nav-item.interface';
import { IItem } from '../stepper-widget/stepper-widget.component';
import { EnvironmentType } from '@libs/models/environment.type';
import { Router } from '@angular/router';
import { environment } from '@libs/environment';
import { LogoutDialogComponent } from './components/logout-dialog/logout-dialog.component';
import { filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { DOCUMENT } from '@angular/common';
import { NamesEnum } from '@libs/enums/names.enum';
import { ProductStore } from '../../storage/product.store';
import { HeaderStepperHelperService } from '@libs/services/header-stepper-helper.service';
import { ConfirmRemovingAccountDialogComponent } from '@libs/shared/components/header/components/confirm-removing-account-dialog/confirm-removing-account-dialog.component';
import { RemovingAccountDialogComponent } from '@libs/shared/components/header/components/removing-account-dialog/removing-account-dialog.component';
import { UnsubscribeService } from '@libs/shared/services/unsubscribe.service';
import { LangService } from '@libs/shared/services/lang.service';

@Component({
  selector: 'app-header',
  templateUrl: `./templates/${environment.name}/page.html`,
  styleUrls: [`./templates/${environment.name}/style.sass`],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UnsubscribeService],
})
export class HeaderComponent {
  @Input()
  public navItems: NavItem[] = [];

  @Input()
  public webSiteItems: NavItem[] = [];

  @Input()
  public phones: IContact[] = [];

  @Input()
  public isWebSite: boolean = false;

  @Input()
  public landingClasses: string = '';

  @Input()
  public steps: IItem[] = [];

  public hamburger: boolean = false;
  public isLogged$: Observable<boolean> = this.authService.isLoggedIn();
  public tabletWide$: Observable<boolean> = this.deviceService.isTabletWide;
  public isDesktop$: Observable<boolean> = this.deviceService.isDesktop;
  public preview$: Observable<ICalculatePreview> = this.calculateStore.preview$;
  public isPreview$: Observable<boolean> = this.calculateStore.isPreviewVisible;
  public isMenu$: Observable<boolean> = this.clientStore.isMenuVisible$;
  public isStepsVisible: boolean = false;
  public isDropdown: boolean = false;
  public hideStepper: boolean;
  private body!: HTMLBodyElement;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(WINDOW) private readonly window: Window,
    @Inject(LOCATION) private location: Location,
    @Inject(ENVIRONMENT) public environment: EnvironmentType,
    @Self() private destroyStream$: UnsubscribeService,
    @Inject(SESSION_STORAGE) private sessionStorage: Storage,
    @Inject(DATA_LAYER) private dataLayer: IDataLayer[],
    private authService: AuthenticationService,
    private calculateStore: CalculateStore,
    private clientStore: ClientStore,
    private readonly deviceService: DeviceService,
    private router: Router,
    private dialog: MatDialog,
    private productStore: ProductStore,
    private cd: ChangeDetectorRef,
    private headerStepperHelperService: HeaderStepperHelperService,
    public langService: LangService,
  ) {
    this.body = this.document.querySelector('body');
    this.initialize();
  }

  public hamburgerEventHandler(v?: boolean): void {
    this.hamburger = v ?? !this.hamburger;
    this.hamburger ? this.body.classList.add('overflow-hidden') : this.body.classList.remove('overflow-hidden');

    this.hamburger
      ? (this.window.onscroll = () => {
          this.window.scrollTo(0, 0);
        })
      : (this.window.onscroll = () => {
          this.window.scrollTo();
        });
  }

  public logoutHandler(): void {
    if (this.environment.group !== NamesEnum.AVANS) {
      this.logout();
    } else {
      const confirmLogout = this.dialog.open(LogoutDialogComponent, {
        panelClass: 'logout-dialog',
        disableClose: true,
        width: '320px',
      });
      confirmLogout
        .afterClosed()
        .pipe(filter(Boolean), take(1))
        .subscribe(() => {
          this.logout();
        });
    }
  }

  public removingAccountHandler(): void {
    const confirmLogout = this.dialog.open(ConfirmRemovingAccountDialogComponent, {
      panelClass: 'logout-dialog',
      disableClose: true,
      width: '320px',
    });
    confirmLogout
      .afterClosed()
      .pipe(filter(Boolean), take(1))
      .subscribe(() => {
        this.dialog.open(RemovingAccountDialogComponent, {
          panelClass: 'logout-dialog',
          width: '320px',
        });
      });
  }

  public onStepsVisibleChange(v: boolean): void {
    this.isStepsVisible = v;
    this.cd.detectChanges();
  }

  public isDropdownClicked(): void {
    this.isDropdown = !this.isDropdown;
    this.cd.detectChanges();
  }

  public onNavigateToCabinet(): void {
    this.dataLayer.push({
      event: 'click_auth_button',
      text: 'Увійти',
    });
  }

  private initialize(): void {
    this.checkShowStepper();
    this.initPreview();
  }

  private checkShowStepper(): void {
    this.headerStepperHelperService.headerData$.pipe(takeUntil(this.destroyStream$)).subscribe((item) => {
      this.hideStepper = item;
      this.cd.markForCheck();
    });
  }

  private initPreview(): void {
    this.authService
      .isLoggedIn()
      .pipe(
        filter((login: boolean) => login),
        switchMap(() => this.productStore.get()),
        tap((product: ILoanProduct) => {
          this.calculateStore.setProduct(product);
          const shouldInit = Boolean(+this.sessionStorage.getItem('amount') && +this.sessionStorage.getItem('term'));
          if (shouldInit) {
            const term = this.calculateStore.term(product);
            const amount = this.calculateStore.amount(product);
            this.calculateStore.updatePreviewParam(amount, term, product.interest, true);
          }
        }),
        takeUntil(this.destroyStream$),
      )
      .subscribe();
  }

  private logout(): void {
    this.hamburgerEventHandler(false);
    this.hamburger = false;
    this.authService.logout();
    this.calculateStore.showPreview = false;
  }
}
